function normalizeQuery(query) {
  return String(query || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase();
}

function normalizeQueryWithOffsets(value) {
  const source = String(value || '');
  const offsets = [];
  let normalized = '';
  let whitespaceStart = -1;
  let whitespaceEnd = -1;

  for (let index = 0; index < source.length; ) {
    const character = String.fromCodePoint(source.codePointAt(index));
    const characterLength = character.length;

    if (/\s/u.test(character)) {
      if (normalized && whitespaceStart < 0) whitespaceStart = index;
      if (whitespaceStart >= 0) whitespaceEnd = index + characterLength;
      index += characterLength;
      continue;
    }

    if (whitespaceStart >= 0) {
      normalized += ' ';
      offsets.push({ start: whitespaceStart, end: whitespaceEnd });
      whitespaceStart = -1;
      whitespaceEnd = -1;
    }

    const normalizedCharacter = character.toLocaleLowerCase();
    normalized += normalizedCharacter;
    for (let normalizedIndex = 0; normalizedIndex < normalizedCharacter.length; normalizedIndex += 1) {
      offsets.push({ start: index, end: index + characterLength });
    }
    index += characterLength;
  }

  return { normalized, offsets };
}

function findQueryRange(text, query) {
  const normalizedQuery = normalizeQuery(query);
  if (!normalizedQuery) return null;

  const { normalized, offsets } = normalizeQueryWithOffsets(text);
  const matchIndex = normalized.indexOf(normalizedQuery);
  if (matchIndex < 0) return null;

  return {
    start: offsets[matchIndex].start,
    end: offsets[matchIndex + normalizedQuery.length - 1].end,
  };
}

function buildPredictiveSearchUrl(baseUrl, query) {
  const parameters = new URLSearchParams({
    q: query,
    'resources[type]': 'product,article,collection',
    'resources[limit]': '10',
    'resources[limit_scope]': 'each',
    section_id: 'predictive-search',
  });
  const separator = baseUrl.includes('?') ? '&' : '?';

  return `${baseUrl}${separator}${parameters.toString()}`;
}

function buildFullProjectSearchUrl(baseUrl, query, page = 1) {
  const parameters = new URLSearchParams({
    q: normalizeQuery(query),
    type: 'product',
    section_id: 'command-search-project-results',
    page: String(Math.max(1, Number(page) || 1)),
  });
  const separator = baseUrl.includes('?') ? '&' : '?';

  return `${baseUrl}${separator}${parameters.toString()}`;
}

function getFullProjectPayloadMetadata(payload, { baseUrl, page, query }) {
  const expectedQuery = normalizeQuery(query);
  const renderedQuery = String(payload?.dataset.searchTerms || '').trim();
  const renderedPage = Number(payload?.dataset.page);
  const hasNextValue = payload?.dataset.hasNext;

  if (renderedQuery !== expectedQuery) throw new Error('Full project search query metadata mismatch');
  if (!Number.isInteger(renderedPage) || renderedPage !== page) {
    throw new Error('Full project search page metadata mismatch');
  }
  if (hasNextValue !== 'true' && hasNextValue !== 'false') {
    throw new Error('Full project search pagination metadata invalid');
  }

  const hasNext = hasNextValue === 'true';
  const emittedNextUrl = String(payload?.dataset.nextUrl || '').trim();
  if (!hasNext) return { hasNext, nextUrl: '', page: renderedPage };
  if (!emittedNextUrl) throw new Error('Full project search next URL missing');

  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://command-search.invalid';
  const base = new URL(baseUrl, origin);
  const next = new URL(emittedNextUrl, origin);

  if (
    next.origin !== base.origin ||
    next.pathname !== base.pathname ||
    next.searchParams.get('q') !== expectedQuery ||
    next.searchParams.get('type') !== 'product' ||
    next.searchParams.get('section_id') !== 'command-search-project-results' ||
    Number(next.searchParams.get('page')) !== renderedPage + 1
  ) {
    throw new Error('Full project search next URL metadata mismatch');
  }

  return { hasNext, nextUrl: next.toString(), page: renderedPage };
}

function isFullResultComplete(metadata) {
  return metadata.hasNext === false;
}

function getFullProjectLoadAction({ loading, shown, loaded, hasNext }) {
  if (loading) return 'ignore';
  if (shown < loaded) return 'reveal';
  if (hasNext) return 'fetch';
  return 'done';
}

function isFullRequestCurrent(generation, activeGeneration, requestQuery, visibleQuery) {
  return generation === activeGeneration && normalizeQuery(requestQuery) === normalizeQuery(visibleQuery);
}

function isFullProjectQueryStale(fullProjectQuery, visibleQuery) {
  const normalizedFullQuery = normalizeQuery(fullProjectQuery);
  return normalizedFullQuery !== '' && normalizedFullQuery !== normalizeQuery(visibleQuery);
}

function getResultTypeViewModel({
  type,
  count,
  shown,
  scope,
  fullProjectPage,
  fullProjectHasNext,
  fullProjectPredictiveIncomplete,
  queryMatches,
}) {
  const hasFullProjectMetadata = type === 'projects' && fullProjectPage > 0 && queryMatches;
  const predictiveProjectIncomplete = type === 'projects' && Boolean(fullProjectPredictiveIncomplete);
  const incomplete = hasFullProjectMetadata
    ? fullProjectPredictiveIncomplete || !isFullResultComplete({ hasNext: fullProjectHasNext })
    : predictiveProjectIncomplete || count === 10;
  const serverCompletenessUnknown =
    type === 'projects' &&
    (hasFullProjectMetadata ? fullProjectHasNext : predictiveProjectIncomplete || count === 10);
  const remaining = Math.max(count - shown, 0);
  const showGroup = scope === 'all' ? count > 0 : type === scope;

  if (scope === 'all') {
    return {
      actionHidden: count <= 4,
      actionMode: 'show-all',
      countLabel: incomplete ? `${count}+` : String(count),
      incomplete,
      remaining,
      showGroup,
    };
  }

  return {
    actionHidden: !serverCompletenessUnknown && remaining === 0,
    actionMode: serverCompletenessUnknown ? 'generic' : remaining > 0 ? 'remaining' : 'none',
    countLabel: incomplete ? `${count}+` : String(count),
    incomplete,
    remaining,
    showGroup,
  };
}

function replaceResultCountLabel(label, previousCount, countLabel) {
  const source = String(label || '');
  const candidates = [`${previousCount}+`, String(previousCount)];
  const previousLabel = candidates.find((candidate) => source.includes(candidate));
  return previousLabel ? source.replace(previousLabel, countLabel) : source;
}

function getEscapeAction(query) {
  return normalizeQuery(query) ? 'clear' : 'close';
}

function getScrollBehavior(prefersReducedMotion) {
  return prefersReducedMotion ? 'auto' : 'smooth';
}

function getSuggestedQuery(url, label, baseUrl, explicitQuery) {
  const explicit = String(explicitQuery || '').trim();
  if (explicit) return explicit;

  const fallback = String(label || '').trim();
  if (!url) return fallback;

  try {
    return new URL(url, baseUrl).searchParams.get('q')?.trim() || fallback;
  } catch {
    return fallback;
  }
}

function getNextSelectionIndex(currentIndex, direction, resultCount) {
  if (resultCount === 0) return -1;
  if (direction === 'up') return currentIndex <= 0 ? resultCount - 1 : currentIndex - 1;

  return currentIndex < 0 || currentIndex >= resultCount - 1 ? 0 : currentIndex + 1;
}

function rankResults(results, query) {
  const normalizedQuery = normalizeQuery(query);

  return [...results].sort((first, second) => {
    const firstMatch = normalizeQuery(first.title).indexOf(normalizedQuery);
    const secondMatch = normalizeQuery(second.title).indexOf(normalizedQuery);

    if (firstMatch >= 0 && secondMatch < 0) return -1;
    if (firstMatch < 0 && secondMatch >= 0) return 1;
    if (firstMatch >= 0 && secondMatch >= 0 && firstMatch !== secondMatch) return firstMatch - secondMatch;

    return Number(second.year || 0) - Number(first.year || 0);
  });
}

function getVisibleResults(results, scope, shown, blendCap = 4) {
  if (scope === 'all') {
    return ['projects', 'news', 'lab', 'collections'].flatMap((type) =>
      results.filter((result) => result.type === type).slice(0, blendCap),
    );
  }

  return results.filter((result) => result.type === scope).slice(0, shown);
}

function isRequestCurrent(requestId, activeRequestId) {
  return requestId === activeRequestId;
}

function debounce(callback, wait) {
  let timeout;

  return (...args) => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => callback(...args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-command-search-form]');
    this.input = this.querySelector('[data-command-search-input]');
    this.clearButton = this.querySelector('[data-command-search-clear]');
    this.status = this.querySelector('[data-command-search-status]');
    this.viewport = this.querySelector('[data-command-search-viewport]');
    this.trending = this.querySelector('[data-command-search-trending]');
    this.keepTyping = this.querySelector('[data-command-search-keep-typing]');
    this.scopes = this.querySelector('[data-command-search-scopes]');
    this.resultsRoot = this.querySelector('[data-predictive-search]');
    this.empty = this.querySelector('[data-command-search-empty]');
    this.error = this.querySelector('[data-command-search-error]');
    this.summary = this.querySelector('[data-command-search-summary]');
    this.defaultSummary = this.summary?.textContent.trim() || '';
    this.cache = new Map();
    this.activeRequestId = 0;
    this.scope = 'all';
    this.shown = 8;
    this.results = [];
    this.fullProjectGeneration = 0;
    this.fullProjectPage = 0;
    this.fullProjectHasNext = false;
    this.fullProjectPredictiveIncomplete = false;
    this.fullProjectLoading = false;
    this.fullProjectQuery = '';
    this.fullProjectIds = new Set();
    this.fullProjectNextUrl = '';

    if (!this.input || !this.resultsRoot) return;

    this.onInput = debounce(this.handleInput.bind(this), 250);
    this.onCompositionEnd = this.handleCompositionEnd.bind(this);
    this.onReset = this.handleReset.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onPointerOver = this.handlePointerOver.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('compositionend', this.onCompositionEnd);
    this.form?.addEventListener('reset', this.onReset);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('click', this.onClick);
    this.resultsRoot.addEventListener('pointerover', this.onPointerOver);
    this.resultsRoot.addEventListener('focusin', this.onFocusIn);
    this.showState('trending');
  }

  disconnectedCallback() {
    this.requestController?.abort();
    this.cancelFullProjectRequest();
    this.cancelAnnouncement();
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('compositionend', this.onCompositionEnd);
    this.form?.removeEventListener('reset', this.onReset);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('click', this.onClick);
    this.resultsRoot?.removeEventListener('pointerover', this.onPointerOver);
    this.resultsRoot?.removeEventListener('focusin', this.onFocusIn);
  }

  focusInput() {
    window.requestAnimationFrame(() => {
      this.input?.focus();
      const end = this.input?.value.length || 0;
      this.input?.setSelectionRange(end, end);
    });
  }

  handleEscape() {
    const action = getEscapeAction(this.input?.value);
    if (action === 'clear') this.clear();
    return action;
  }

  handleCompositionEnd() {
    this.handleInput();
  }

  handleInput(event) {
    if (event?.isComposing) return;
    const query = normalizeQuery(this.input.value);
    this.clearButton.hidden = query.length === 0;

    if (query.length === 0) {
      this.cancelRequest();
      this.resetFullProjectState();
      this.resultsRoot.replaceChildren();
      this.results = [];
      this.showState('trending');
      return;
    }

    if (query.length < 3) {
      this.cancelRequest();
      this.resetFullProjectState(query);
      this.showState('keep-typing');
      return;
    }

    if (query !== this.fullProjectQuery) {
      this.resetFullProjectState(query);
    }

    this.fetchResults(query);
  }

  handleReset(event) {
    event.preventDefault();
    this.clear();
  }

  clear({ focus = true } = {}) {
    this.cancelRequest();
    this.resetFullProjectState();
    this.input.value = '';
    this.clearButton.hidden = true;
    this.scope = 'all';
    this.shown = 8;
    this.results = [];
    this.resultsRoot.replaceChildren();
    this.showState('trending');
    if (focus) this.focusInput();
  }

  cancelRequest() {
    this.activeRequestId += 1;
    this.requestController?.abort();
    this.requestController = null;
  }

  cancelFullProjectRequest() {
    this.fullProjectGeneration += 1;
    this.fullProjectController?.abort();
    this.fullProjectController = null;
    this.fullProjectLoading = false;
  }

  resetFullProjectState(query = '') {
    this.cancelFullProjectRequest();
    this.fullProjectPage = 0;
    this.fullProjectHasNext = false;
    this.fullProjectPredictiveIncomplete = false;
    this.fullProjectQuery = normalizeQuery(query);
    this.fullProjectIds = new Set();
    this.fullProjectNextUrl = '';
  }

  async fetchResults(query) {
    const locale = this.dataset.locale || document.documentElement.lang || 'default';
    const cacheKey = `${locale}:${normalizeQuery(query)}`;

    this.scope = 'all';
    this.shown = 8;
    this.showState('loading');

    if (this.cache.has(cacheKey)) {
      this.cancelRequest();
      this.renderResponse(this.cache.get(cacheKey), query);
      return;
    }

    this.requestController?.abort();
    this.requestController = new AbortController();
    const requestId = ++this.activeRequestId;

    try {
      const response = await fetch(buildPredictiveSearchUrl(this.dataset.predictiveUrl, query), {
        headers: { Accept: 'text/html' },
        signal: this.requestController.signal,
      });

      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

      const markup = await response.text();
      if (!isRequestCurrent(requestId, this.activeRequestId)) return;

      this.cache.set(cacheKey, markup);
      this.renderResponse(markup, query);
    } catch (error) {
      if (error.name === 'AbortError' || !isRequestCurrent(requestId, this.activeRequestId)) return;
      this.showState('error');
      this.announce(this.dataset.errorText);
    }
  }

  renderResponse(markup, query) {
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const response = documentFragment.querySelector('[data-command-search-response]');
    this.resetFullProjectState(query);

    if (!response) {
      this.resultsRoot.replaceChildren();
      this.results = [];
      this.showState('empty');
      this.announce(this.empty.querySelector('.command-search__empty-title')?.textContent.trim());
      return;
    }

    const predictiveProjectGroup = response.querySelector?.('[data-command-search-group="projects"]');
    this.fullProjectPredictiveIncomplete = predictiveProjectGroup?.dataset.predictiveIncomplete === 'true';
    this.resultsRoot.replaceChildren(response);
    this.rebuildResults(query);
    if (Number(response.dataset.resultCount) === 0) {
      this.showState('empty');
      this.announce(this.empty.querySelector('.command-search__empty-title')?.textContent.trim());
    } else {
      this.showState('results');
      this.announceResults(query);
    }

    void this.fetchFullProjects(null, 1);
  }

  rebuildResults(query) {
    this.results = [...this.resultsRoot.querySelectorAll('[data-command-search-row]')].map((element) => ({
      element,
      title: element.dataset.resultTitle || '',
      type: element.dataset.resultType,
      year: Number(element.dataset.resultYear || 0),
    }));
    this.results.forEach((result, index) => {
      result.element.id = `${this.input.id}-result-${index + 1}`;
    });

    this.sortAndHighlight(query);
    this.updateCounts();
    this.updateView();
  }

  sortAndHighlight(query) {
    const rankedResults = [];
    for (const type of ['projects', 'news', 'lab', 'collections']) {
      const ranked = rankResults(
        this.results.filter((result) => result.type === type),
        query,
      );
      rankedResults.push(...ranked);
      const list = this.resultsRoot.querySelector(`[data-command-search-group="${type}"] ul`);
      ranked.forEach((result) => list?.append(result.element.closest('[data-command-search-item]')));
    }

    this.results = rankedResults;
    this.results.forEach((result) => this.highlightTitle(result.element, query));
    this.resultsRoot.querySelectorAll('[data-command-search-show-all]').forEach((action) => {
      if (!action.dataset.showAllLabel) action.dataset.showAllLabel = action.textContent.trim();
    });
  }

  highlightTitle(row, query) {
    const title = row.querySelector('[data-command-search-title]');
    if (!title) return;

    const text = title.textContent;
    const matchRange = findQueryRange(text, query);
    title.replaceChildren();

    if (!matchRange) {
      title.textContent = text;
      return;
    }

    title.append(document.createTextNode(text.slice(0, matchRange.start)));
    const mark = document.createElement('mark');
    mark.textContent = text.slice(matchRange.start, matchRange.end);
    title.append(mark, document.createTextNode(text.slice(matchRange.end)));
  }

  updateCounts() {
    const totalCount = this.results.length;
    const totalIsCapped = ['projects', 'news', 'lab', 'collections'].some(
      (type) => this.getResultTypeView(type).incomplete,
    );
    const totalCountNode = this.querySelector('[data-command-search-count="all"]');
    if (totalCountNode) totalCountNode.textContent = totalIsCapped ? `${totalCount}+` : String(totalCount);

    for (const type of ['projects', 'news', 'lab', 'collections']) {
      const count = this.results.filter((result) => result.type === type).length;
      const view = this.getResultTypeView(type, count);
      const countNode = this.querySelector(`[data-command-search-count="${type}"]`);
      const scopeButton = this.querySelector(`[data-command-search-scope="${type}"]`);
      const group = this.resultsRoot.querySelector(`[data-command-search-group="${type}"]`);
      const previousCount = group?.dataset.resultCount ?? String(count);
      const groupCount = group?.querySelector('[data-command-search-group-count]');
      const action = group?.querySelector('[data-command-search-show-all]');

      if (countNode) countNode.textContent = count ? view.countLabel : '';
      if (scopeButton) scopeButton.hidden = count === 0;
      if (groupCount) groupCount.textContent = view.countLabel;
      if (action?.dataset.showAllLabel) {
        action.dataset.showAllLabel = replaceResultCountLabel(
          action.dataset.showAllLabel,
          previousCount,
          view.countLabel,
        );
      }
      if (group) group.dataset.resultCount = String(count);
    }
  }

  getResultTypeView(type, count = this.results.filter((result) => result.type === type).length) {
    return getResultTypeViewModel({
      type,
      count,
      shown: this.shown,
      scope: this.scope,
      fullProjectPage: this.fullProjectPage,
      fullProjectHasNext: this.fullProjectHasNext,
      fullProjectPredictiveIncomplete: this.fullProjectPredictiveIncomplete,
      queryMatches: normalizeQuery(this.input?.value) === this.fullProjectQuery,
    });
  }

  updateView() {
    const visibleResults = getVisibleResults(this.results, this.scope, this.shown);
    const visibleElements = new Set(visibleResults.map((result) => result.element));

    for (const group of this.resultsRoot.querySelectorAll('[data-command-search-group]')) {
      const type = group.dataset.commandSearchGroup;
      const typeResults = this.results.filter((result) => result.type === type);
      const view = this.getResultTypeView(type, typeResults.length);
      group.hidden = !view.showGroup;

      for (const row of group.querySelectorAll('[data-command-search-row]')) {
        row.closest('[data-command-search-item]').hidden = !visibleElements.has(row);
      }

      const action = group.querySelector('[data-command-search-show-all]');
      if (!action) continue;

      if (this.scope === 'all') {
        action.hidden = view.actionHidden;
        action.removeAttribute('data-command-search-load-more');
        action.textContent = action.dataset.showAllLabel;
      } else {
        action.hidden = view.actionHidden;
        action.dataset.commandSearchLoadMore = type;
        if (view.actionMode === 'generic') {
          action.textContent = this.dataset.loadMoreText;
        } else if (view.actionMode === 'remaining') {
          const label = document.createElement('span');
          const count = document.createElement('span');
          label.textContent = this.dataset.loadMoreText;
          count.className = 'command-search__load-more-count';
          count.textContent = this.interpolate(this.dataset.loadMoreCountText, { count: view.remaining });
          action.replaceChildren();
          action.append(label, count);
        }
      }
    }

    this.querySelectorAll('[data-command-search-scope]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.commandSearchScope === this.scope));
    });

    this.clearSelection();
  }

  setScope(scope) {
    if (scope !== 'all' && !this.results.some((result) => result.type === scope)) scope = 'all';
    this.scope = scope;
    this.shown = 8;
    this.updateView();
    this.viewport.scrollTo({ top: 0, behavior: 'auto' });
  }

  async handleClick(event) {
    const suggestion = event.target.closest('[data-command-search-query]');
    if (suggestion) {
      this.input.value = getSuggestedQuery(
        suggestion.dataset.commandSearchUrl,
        suggestion.textContent,
        window.location.origin,
        suggestion.dataset.commandSearchQueryValue,
      );
      this.clearButton.hidden = false;
      this.handleInput();
      this.focusInput();
      return;
    }

    const scope = event.target.closest('[data-command-search-scope]');
    if (scope) {
      this.setScope(scope.dataset.commandSearchScope);
      return;
    }

    const action = event.target.closest('[data-command-search-show-all]');
    if (!action) return;

    const type = action.dataset.commandSearchShowAll;
    if (type === 'projects' && isFullProjectQueryStale(this.fullProjectQuery, this.input?.value)) return;

    if (action.hasAttribute('data-command-search-load-more')) {
      const typeResults = this.results.filter((result) => result.type === type);
      const loadAction =
        type === 'projects'
          ? getFullProjectLoadAction({
              loading: this.fullProjectLoading,
              shown: this.shown,
              loaded: typeResults.length,
              hasNext:
                this.fullProjectPage === 0
                  ? this.fullProjectPredictiveIncomplete || typeResults.length === 10
                  : this.fullProjectHasNext,
            })
          : this.shown < typeResults.length
            ? 'reveal'
            : 'done';
      if (loadAction === 'ignore' || loadAction === 'done') return;

      const previousRows = new Set(this.getVisibleRows());
      const fetchedFromServer = loadAction === 'fetch';
      if (loadAction === 'fetch') {
        this.shown = typeResults.length + 8;
        const nextPage = this.fullProjectPage === 0 ? 1 : this.fullProjectPage + 1;
        const loaded = await this.fetchFullProjects(action, nextPage);
        if (!loaded) return;
      } else {
        this.shown += 8;
        this.updateView();
      }
      const newlyVisibleRows = this.getVisibleRows().filter((row) => !previousRows.has(row));
      const firstNewRow = newlyVisibleRows[0];
      if (firstNewRow) this.selectRow(firstNewRow, true);
      if (type === 'projects' && fetchedFromServer && newlyVisibleRows.length === 0 && !this.fullProjectHasNext) {
        this.recoverFullProjectFocus();
        this.announceResults(this.fullProjectQuery);
        return;
      }
      this.announce(this.interpolate(this.dataset.resultsAddedText, { count: newlyVisibleRows.length }));
    } else {
      this.setScope(type);
      this.querySelector(`[data-command-search-scope="${type}"]`)?.focus();
      if (type === 'projects' && this.fullProjectPage === 0) await this.fetchFullProjects(action, 1);
    }
  }

  async fetchFullProjects(action, page = this.fullProjectPage + 1) {
    if (this.fullProjectLoading) return false;
    if (isFullProjectQueryStale(this.fullProjectQuery, this.input.value)) return false;

    const query = normalizeQuery(this.input.value);
    if (query.length < 3) return false;

    const firstRequestedPage = Math.max(1, Number(page) || 1);
    let currentPage = firstRequestedPage;
    let hasNext = this.fullProjectHasNext;
    let nextUrl = this.fullProjectNextUrl;
    let requestUrl =
      firstRequestedPage === this.fullProjectPage + 1 && this.fullProjectNextUrl
        ? this.fullProjectNextUrl
        : buildFullProjectSearchUrl(this.dataset.fullSearchUrl, query, firstRequestedPage);
    const acceptedRows = [];
    const previousResultIds = this.fullProjectIds;
    const previousNextUrl = this.fullProjectNextUrl;
    const previousPredictiveIncomplete = this.fullProjectPredictiveIncomplete;
    const generation = this.fullProjectGeneration;
    const controller = new AbortController();

    const existingProjectResults =
      firstRequestedPage === 1 ? this.results.filter((result) => result.type === 'projects') : [];
    const predictiveProjectGroup =
      firstRequestedPage === 1
        ? this.resultsRoot.querySelector('[data-command-search-group="projects"]')
        : null;
    const existingProjectIds = firstRequestedPage === 1
      ? existingProjectResults.map((result) => result.element.dataset.resultId).filter(Boolean)
      : this.fullProjectIds;
    this.fullProjectIds = new Set(existingProjectIds);
    if (firstRequestedPage === 1) {
      this.fullProjectPredictiveIncomplete = predictiveProjectGroup?.dataset.predictiveIncomplete === 'true';
    }
    this.fullProjectController = controller;
    this.fullProjectLoading = true;
    if (action) action.disabled = true;
    this.announce(this.dataset.loadingText);

    try {
      do {
        const response = await fetch(requestUrl, {
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Full project search returned ${response.status}`);

        const markup = await response.text();
        if (!isFullRequestCurrent(generation, this.fullProjectGeneration, query, this.input.value)) return false;

        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const payload = documentFragment.querySelector('[data-command-search-full-projects]');
        if (!payload) throw new Error('Full project search payload missing');

        const metadata = getFullProjectPayloadMetadata(payload, {
          baseUrl: this.dataset.fullSearchUrl,
          page: currentPage,
          query,
        });
        hasNext = metadata.hasNext;
        nextUrl = metadata.nextUrl;
        const incomingRows = [...payload.querySelectorAll('[data-command-search-row]')];

        const uniqueRows = incomingRows.filter((row) => {
          const id = row.dataset.resultId;
          if (!id || this.fullProjectIds.has(id)) return false;
          this.fullProjectIds.add(id);
          return true;
        });
        acceptedRows.push(...uniqueRows);

        if (uniqueRows.length > 0 || !hasNext) break;
        currentPage = metadata.page + 1;
        requestUrl = nextUrl;
      } while (hasNext);

      if (!isFullRequestCurrent(generation, this.fullProjectGeneration, query, this.input.value)) return false;

      const projectList = this.resultsRoot.querySelector('[data-command-search-group="projects"] ul');
      if (!projectList) throw new Error('Project result list missing');
      const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
      const projectGroup = projectList.closest?.('[data-command-search-group="projects"]');
      const recoverProjectFocus =
        this.scope === 'projects' ||
        action === activeElement ||
        Boolean(activeElement && projectGroup?.contains(activeElement));
      const items = acceptedRows
        .map((row) => row.closest('[data-command-search-item]'))
        .filter(Boolean);

      projectList.append(...items);

      this.fullProjectPage = currentPage;
      this.fullProjectHasNext = hasNext;
      this.fullProjectNextUrl = nextUrl;
      this.rebuildResults(query);
      this.reconcileFullProjectResults({ recoverFocus: recoverProjectFocus });
      this.announceResults(query);
      return true;
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        !isFullRequestCurrent(generation, this.fullProjectGeneration, query, this.input.value)
      ) {
        return false;
      }
      this.fullProjectIds = previousResultIds;
      this.fullProjectNextUrl = previousNextUrl;
      this.fullProjectPredictiveIncomplete = previousPredictiveIncomplete;
      this.announce(this.dataset.errorText);
      return false;
    } finally {
      if (action) action.disabled = false;
      if (generation === this.fullProjectGeneration && this.fullProjectController === controller) {
        this.fullProjectController = null;
        this.fullProjectLoading = false;
      }
    }
  }

  reconcileFullProjectResults({ recoverFocus = this.scope === 'projects' } = {}) {
    if (this.results.some((result) => result.type === 'projects')) {
      this.showState('results');
      if (recoverFocus) this.recoverFullProjectFocus();
      return 'projects';
    }

    if (this.results.length > 0) {
      this.scope = 'all';
      this.shown = 8;
      this.updateView();
      if (recoverFocus) {
        const allScope = this.querySelector('[data-command-search-scope="all"]');
        if (allScope && !allScope.hidden) {
          allScope.focus();
        } else {
          this.input?.focus();
        }
      }
      return 'all';
    }

    this.showState('empty');
    this.input?.focus();
    return 'empty';
  }

  recoverFullProjectFocus() {
    const projectScope = this.querySelector?.('[data-command-search-scope="projects"]');
    if (projectScope && !projectScope.hidden) {
      projectScope.focus();
      return 'scope';
    }

    const visibleProjectRow = this.getVisibleRows().find(
      (row) => row.dataset.resultType === 'projects' || !row.dataset.resultType,
    );
    if (visibleProjectRow) {
      this.selectRow(visibleProjectRow, true);
      return 'row';
    }

    this.input?.focus();
    return 'input';
  }

  handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    const scopeButton = event.target.closest('[data-command-search-scope]');
    if (scopeButton && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const buttons = [...this.querySelectorAll('[data-command-search-scope]:not([hidden])')];
      const currentIndex = buttons.indexOf(scopeButton);
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const nextButton = buttons[(currentIndex + direction + buttons.length) % buttons.length];
      this.setScope(nextButton.dataset.commandSearchScope);
      nextButton.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = this.getVisibleRows();
    if (!rows.length) return;

    event.preventDefault();
    const currentIndex = rows.indexOf(event.target.closest('[data-command-search-row]'));
    const nextIndex = getNextSelectionIndex(currentIndex, event.key === 'ArrowUp' ? 'up' : 'down', rows.length);
    this.selectRow(rows[nextIndex], true);
  }

  handlePointerOver(event) {
    const row = event.target.closest('[data-command-search-row]');
    if (row && !row.closest('[data-command-search-item]').hidden) this.selectRow(row, false);
  }

  handleFocusIn(event) {
    const row = event.target.closest('[data-command-search-row]');
    if (row) this.selectRow(row, false);
  }

  getVisibleRows() {
    return [...this.resultsRoot.querySelectorAll('[data-command-search-row]')].filter(
      (row) => !row.closest('[data-command-search-item]').hidden && !row.closest('[data-command-search-group]').hidden,
    );
  }

  selectRow(row, focus) {
    this.clearSelection();
    row.classList.add('is-selected');
    if (focus) {
      row.focus({ preventScroll: true });
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      row.scrollIntoView({ block: 'nearest', behavior: getScrollBehavior(prefersReducedMotion) });
    }
  }

  clearSelection() {
    this.resultsRoot.querySelectorAll('.is-selected').forEach((row) => row.classList.remove('is-selected'));
  }

  showState(state) {
    this.trending.hidden = state !== 'trending';
    this.keepTyping.hidden = state !== 'keep-typing';
    this.scopes.hidden = state !== 'results';
    this.resultsRoot.hidden = state !== 'results';
    this.empty.hidden = state !== 'empty';
    this.error.hidden = state !== 'error';
    this.toggleAttribute('aria-busy', state === 'loading');

    if (this.summary) this.summary.textContent = state === 'results' ? this.summary.textContent : this.defaultSummary;
    if (state === 'loading') {
      this.announce(this.dataset.loadingText);
    } else if (state === 'keep-typing') {
      this.announce(this.keepTyping.textContent.trim());
    } else if (state === 'trending') {
      this.announce('');
    }
  }

  announceResults(query) {
    const count = this.results.length;
    const countLabel = ['projects', 'news', 'lab', 'collections'].some(
      (type) => this.getResultTypeView(type).incomplete,
    )
      ? `${count}+`
      : count;
    const text = this.interpolate(this.dataset.resultsText, { count: countLabel, query });
    if (this.summary) this.summary.textContent = text;
    this.announce(text);
  }

  announce(message) {
    if (!this.status) return;
    this.cancelAnnouncement();
    this.status.textContent = '';
    this.announcementFrame = window.requestAnimationFrame(() => {
      this.status.textContent = message || '';
      this.announcementFrame = null;
    });
  }

  cancelAnnouncement() {
    if (this.announcementFrame == null) return;
    window.cancelAnimationFrame(this.announcementFrame);
    this.announcementFrame = null;
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`__${key.toUpperCase()}__`, String(value)),
      template || '',
    );
  }
}

if (typeof customElements.get !== 'function' || !customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

if (typeof module !== 'undefined') {
  module.exports = {
    buildFullProjectSearchUrl,
    buildPredictiveSearchUrl,
    findQueryRange,
    getEscapeAction,
    getFullProjectLoadAction,
    getResultTypeViewModel,
    getScrollBehavior,
    getNextSelectionIndex,
    getSuggestedQuery,
    getVisibleResults,
    isFullResultComplete,
    isFullRequestCurrent,
    isRequestCurrent,
    normalizeQuery,
    PredictiveSearch,
    rankResults,
  };
}
